// One page of the report PDF drawn on a canvas (pdf.js) for the paged preview: as sharp as the screen allows, then
// scaled by the sheet's CSS size and zoom.
import { memo, useEffect, useRef, type FC } from "react";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";

type ReportPdfPageProps = {
  doc: PDFDocumentProxy;
  // 1-based
  pageNumber: number;
  className?: string;
};

// canvas pixels per CSS pixel of the 794 px wide sheet: enough for zooming in a step or two
const RENDER_DENSITY = 2;
// the sheet's width in CSS pixels (A4 at 96 dpi)
const SHEET_WIDTH = 794;

const ReportPdfPage: FC<ReportPdfPageProps> = ({ doc, pageNumber, className }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let task: RenderTask | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const page = await doc.getPage(pageNumber);
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        const density = Math.max(RENDER_DENSITY, window.devicePixelRatio || 1);
        const viewport = page.getViewport({ scale: (SHEET_WIDTH * density) / page.getViewport({ scale: 1 }).width });
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        task = page.render({ canvas, viewport });
        await task.promise;
      } catch {
        // cancelled, or a document replaced meanwhile: the new one draws its own pages
      }
    })();
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, pageNumber]);

  return <canvas ref={canvasRef} className={className} />;
};

export default memo(ReportPdfPage);
